import React, { useEffect, useContext, useState, useMemo } from 'react';
import { Row, Col, Button, Badge, Table } from 'react-bootstrap';
import { ProductContext } from '../../ProductProvider';
import { ChartsGoogle } from './ChartsGoogle';
import { BarChartsGoogle } from './BarChartsGoogle';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { fetchAuthSession } from 'aws-amplify/auth';
import Preloader from '../../components/Preloader/Preloader';
import './Reports.css';

const config = require('../../config.json');

export default function IssueReport(props) {
  const { generateDate, generateReturnDate } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  const [issueDetails, setIssueDetails] = useState({ dueBooks: [], nextDueBooks: [] });
  const [googleIssueData, setGoogleIssueData] = useState();
  const [isIssueDayCount, setIsIssueDayCount] = useState(false);
  const [googleReturnData, setGoogleReturnData] = useState();
  const [isReturnDayCount, setIsReturnDayCount] = useState(false);
  const [googleUserData, setGoogleUserData] = useState();
  const [isUserDayCount, setIsUserDayCount] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    handleIssueSearch();
  }, []);

  const handleIssueSearch = async () => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = 'VJCollege';

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/reports/issue?clientID=${clientID}`, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      if (data.length > 0) {
        await processIssueReports(data);
        initializeValues(data);
      }
      enqueueSnackbar('Circulation & Issue Report loaded successfully', { variant: 'success' });
      setLoaded(true);
    } catch (error) {
      console.error('Error loading issue report:', error);
      enqueueSnackbar('Error Occurred - ' + error.message, { variant: 'warning' });
      setLoaded(true);
    }
  };

  const processIssueReports = async (tsearchResult) => {
    const todayDate = await generateDate();
    const nextDueDate = await generateReturnDate(7);
    const dueBooks = tsearchResult.filter((item) => item.bookedTo && item.bookedTo < todayDate);
    const nextSevenDays = tsearchResult.filter(
      (item) => item.bookedTo && item.bookedTo <= nextDueDate && item.bookedTo >= todayDate
    );

    setIssueDetails({
      dueBooks: dueBooks,
      nextDueBooks: nextSevenDays
    });

    await processIssueTrendData(tsearchResult);
    await processReturnTrendData(tsearchResult);
  };

  const processIssueTrendData = async (tsearchResult) => {
    const dayIssueCount = {};
    for (let i = -30; i <= 0; i++) {
      const nextDueDate = await generateReturnDate(i);
      const dueBooks = tsearchResult.filter((item) => item.bookedOn === nextDueDate);
      const ttemp = nextDueDate.toString();
      dayIssueCount[ttemp] = dueBooks.length;
    }
    processData(dayIssueCount, 'Issue');
  };

  const processReturnTrendData = async (tsearchResult) => {
    const dayIssueCount = {};
    for (let i = 0; i <= 30; i++) {
      const nextDueDate = await generateReturnDate(i);
      const dueBooks = tsearchResult.filter((item) => item.bookedTo === nextDueDate);
      const ttemp = nextDueDate.toString();
      dayIssueCount[ttemp] = dueBooks.length;
    }
    processData(dayIssueCount, 'Return');
  };

  const processData = (rawData, type) => {
    const regChartHeader = [];
    let chartHeader = '';
    if (type === 'Issue') {
      chartHeader = ['Date', "Book's Issued"];
    } else if (type === 'User') {
      chartHeader = ['User', "Book's Count"];
    } else {
      chartHeader = ['Date', "Book's Return"];
    }
    regChartHeader.push(chartHeader);

    for (const data in rawData) {
      regChartHeader.push([data, rawData[data]]);
    }

    if (type === 'Issue') {
      setGoogleIssueData(regChartHeader);
      setIsIssueDayCount(true);
    } else if (type === 'User') {
      setGoogleUserData(regChartHeader);
      setIsUserDayCount(true);
    } else {
      setGoogleReturnData(regChartHeader);
      setIsReturnDayCount(true);
    }
  };

  const initializeValues = (tsearchResult) => {
    const uniqueArray = removeDuplicates('bookedfor', tsearchResult);
    const sortedArray = uniqueArray.sort((a, b) => {
      const nameA = a.bookedfor || '';
      const nameB = b.bookedfor || '';
      return nameA.localeCompare(nameB);
    });
    processUserTrendData(sortedArray, tsearchResult);
  };

  const removeDuplicates = (tprop, tsearchResult) => {
    const newArray = [];
    const lookupObject = {};
    for (const i in tsearchResult) {
      if (tsearchResult[i][tprop]) {
        lookupObject[tsearchResult[i][tprop]] = tsearchResult[i];
      }
    }
    for (const i in lookupObject) {
      newArray.push(lookupObject[i]);
    }
    return newArray;
  };

  const processUserTrendData = async (sortedArray, tsearchResult) => {
    const dayIssueCount = {};
    // Top 10 borrowers to keep chart neat and legible
    const sample = sortedArray.slice(0, 10);
    for (const item of sample) {
      const dueBooks = tsearchResult.filter((b) => b.bookedfor === item.bookedfor);
      dayIssueCount[item.bookedfor] = dueBooks.length;
    }
    processData(dayIssueCount, 'User');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="reports-section">
      {!loaded && <Preloader show={true} />}

      {/* Printable Header */}
      <div className="print-only-header">
        <h2>Vidyajyoti College of Theology - Library</h2>
        <h4>Circulation & Issue Analytics Report</h4>
        <p>Generated On: {new Date().toLocaleDateString('en-GB')}</p>
        <hr />
      </div>

      {/* KPI Cards Grid */}
      <div className="reports-kpi-grid">
        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-primary bg-opacity-10 text-primary">
            <i className="bi bi-book-half"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-primary">{searchResult.length}</div>
            <div className="reports-kpi-lbl">Total Books Issued</div>
            <div className="reports-kpi-sub">Active loans recorded</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-danger bg-opacity-10 text-danger">
            <i className="bi bi-exclamation-triangle"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-danger">{issueDetails.dueBooks.length}</div>
            <div className="reports-kpi-lbl">Overdue Books</div>
            <div className="reports-kpi-sub">Passed return deadline</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-warning bg-opacity-10 text-warning">
            <i className="bi bi-clock-history"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-warning">{issueDetails.nextDueBooks.length}</div>
            <div className="reports-kpi-lbl">Due in Next 7 Days</div>
            <div className="reports-kpi-sub">Upcoming return forecast</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-success bg-opacity-10 text-success">
            <i className="bi bi-arrow-repeat"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-success">
              {searchResult.length > 0 ? `${Math.round(((searchResult.length - issueDetails.dueBooks.length) / searchResult.length) * 100)}%` : '100%'}
            </div>
            <div className="reports-kpi-lbl">On-Time Health</div>
            <div className="reports-kpi-sub">Non-overdue circulation ratio</div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="fw-bold text-dark small">
          <i className="bi bi-graph-up text-primary me-1"></i> Circulation Trends & Borrower Insights
        </span>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={handleIssueSearch}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh Data
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
            <i className="bi bi-printer me-1"></i> Print Report
          </Button>
        </div>
      </div>

      {/* Charts Grid */}
      <Row className="g-3">
        {isIssueDayCount && (
          <Col lg={6} md={12}>
            <div className="reports-chart-card">
              <div className="reports-chart-title">
                <i className="bi bi-calendar-check text-primary"></i> Last 30 Days - Book Issue Activity
              </div>
              <ChartsGoogle regYearCount={googleIssueData} titleMsg="Last 30 Days Book Issued Trend" />
            </div>
          </Col>
        )}

        {isReturnDayCount && (
          <Col lg={6} md={12}>
            <div className="reports-chart-card">
              <div className="reports-chart-title">
                <i className="bi bi-calendar-event text-success"></i> Next 30 Days - Book Return Schedule
              </div>
              <ChartsGoogle regYearCount={googleReturnData} titleMsg="Next 30 Days Book Return Forecast" />
            </div>
          </Col>
        )}

        {isUserDayCount && (
          <Col lg={12}>
            <div className="reports-chart-card">
              <div className="reports-chart-title">
                <i className="bi bi-people text-info"></i> Active Borrowers Loan Distribution
              </div>
              <BarChartsGoogle regYearCount={googleUserData} titleMsg="Top Active Borrowers" />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}
